import { useRef, useState, useCallback, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
} from "lucide-react";
import "./index.css";

export default function RichTextEditor({ value, onChange, placeholder, onEditorReady, onFocus, onSelectionChange }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && onEditorReady) {
      onEditorReady(editorRef);
    }
  }, [onEditorReady]);
  const [activeFormats, setActiveFormats] = useState([]);

 

  const updateActiveFormats = useCallback(() => {
    const formats = [];
    if (document.queryCommandState("bold")) formats.push("bold");
    if (document.queryCommandState("italic")) formats.push("italic");
    if (document.queryCommandState("underline")) formats.push("underline");
    setActiveFormats(formats);
  }, []);
   const execCommand = useCallback((command, val) => {
    document.execCommand(command, false, val);
    editorRef.current?.focus();
    updateActiveFormats();
  }, [updateActiveFormats]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    updateActiveFormats();
    if (onSelectionChange) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        onSelectionChange(selection.getRangeAt(0).cloneRange());
      }
    }
  }, [onChange, updateActiveFormats, onSelectionChange]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      
      editorRef.current.innerHTML = value || '';
      
      // Restore cursor position if possible
      if (range) {
        try {
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (e) {
          const newRange = document.createRange();
          newRange.selectNodeContents(editorRef.current);
          newRange.collapse(false);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
    }
  }, [value]);

  const handleLink = () => {
    const url = prompt("Enter URL");
    if (url) execCommand("createLink", url);
  };

  const isActive = (cmd) => activeFormats.includes(cmd);

  return (
    <div className="rte-wrapper">
      {/* Toolbar */}
      <div className="rte-toolbar">
        <ToolbarButton
          active={isActive("bold")}
          onClick={() => execCommand("bold")}
          label="Bold"
        >
          <Bold size={16} />
        </ToolbarButton>

        <ToolbarButton
          active={isActive("italic")}
          onClick={() => execCommand("italic")}
          label="Italic"
        >
          <Italic size={16} />
        </ToolbarButton>

        <ToolbarButton
          active={isActive("underline")}
          onClick={() => execCommand("underline")}
          label="Underline"
        >
          <Underline size={16} />
        </ToolbarButton>

        <div className="vr mx-2" />

        <ToolbarButton onClick={handleLink} label="Insert Link">
          <LinkIcon size={16} />
        </ToolbarButton>

        <div className="vr mx-2" />

        <ToolbarButton
          onClick={() => execCommand("insertUnorderedList")}
          label="Bullet List"
        >
          <List size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => execCommand("insertOrderedList")}
          label="Numbered List"
        >
          <ListOrdered size={16} />
        </ToolbarButton>

        <div className="vr mx-2" />

        <ToolbarButton
          onClick={() => execCommand("justifyLeft")}
          label="Align Left"
        >
          <AlignLeft size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => execCommand("justifyCenter")}
          label="Align Center"
        >
          <AlignCenter size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => execCommand("justifyRight")}
          label="Align Right"
        >
          <AlignRight size={16} />
        </ToolbarButton>

        <div className="vr mx-2" />

        <ToolbarButton onClick={() => execCommand("undo")} label="Undo">
          <Undo size={16} />
        </ToolbarButton>

        <ToolbarButton onClick={() => execCommand("redo")} label="Redo">
          <Redo size={16} />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="rte-editor"
        onInput={handleInput}
        onMouseUp={(e) => {
          updateActiveFormats();
          if (onSelectionChange) {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              onSelectionChange(selection.getRangeAt(0).cloneRange());
            }
          }
        }}
        onKeyUp={(e) => {
          updateActiveFormats();
          if (onSelectionChange) {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              onSelectionChange(selection.getRangeAt(0).cloneRange());
            }
          }
        }}
        onFocus={onFocus}
        onClick={onFocus}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
}

/* Toolbar Button */
function ToolbarButton({ children, onClick, active, label }) {
  return (
    <button
      type="button"
      className={`btn btn-sm btn-light rte-btn ${
        active ? "active" : ""
      }`}
      onClick={onClick}
      aria-label={label}
    >
      {children}
    </button>
  );
}
